<template>
  <div class="register-page">
    <AppSidebar />
    
    <div class="register-container">
      <div class="register-box">
        <div class="register-header">
          <h2>用户注册</h2>
          <p>创建您的账号，开始精彩漫画之旅</p>
        </div>
        
        <div class="register-form">
          <div class="form-group" :class="{ 'has-error': errors.account }">
            <label for="account">账号</label>
            <input 
              type="text" 
              id="account" 
              v-model="account" 
              placeholder="请输入用户名或手机号"
              @input="clearError('account')"
            />
            <div class="error-message" v-if="errors.account">{{ errors.account }}</div>
          </div>
          
          <div class="form-group" :class="{ 'has-error': errors.password }">
            <label for="password">密码</label>
            <input 
              type="password" 
              id="password" 
              v-model="password" 
              placeholder="请输入密码"
              @input="clearError('password')"
            />
            <div class="error-message" v-if="errors.password">{{ errors.password }}</div>
          </div>
          
          <div class="form-group" :class="{ 'has-error': errors.confirmPassword }">
            <label for="confirmPassword">确认密码</label>
            <input 
              type="password" 
              id="confirmPassword" 
              v-model="confirmPassword" 
              placeholder="请再次输入密码"
              @input="clearError('confirmPassword')"
            />
            <div class="error-message" v-if="errors.confirmPassword">{{ errors.confirmPassword }}</div>
          </div>
          
          <div class="form-agreement">
            <input type="checkbox" id="agreement" v-model="agreement" />
            <label for="agreement">我已阅读并同意 <a href="#">用户协议</a> 和 <a href="#">隐私政策</a></label>
            <div class="error-message" v-if="errors.agreement">{{ errors.agreement }}</div>
          </div>
          
          <button 
            class="register-button" 
            @click="handleRegister" 
            :disabled="loading"
          >
            <span v-if="!loading">注册</span>
            <span v-else class="loading-spinner"></span>
          </button>
          
          <div class="register-footer">
            <p>已有账号? <router-link to="/login" class="login-link">立即登录</router-link></p>
          </div>
        </div>
      </div>
    </div>
    
    <AppFooter />
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { register } from '@/api/user';
import AppSidebar from '@/components/layout/Sidebar.vue';
import AppFooter from '@/components/layout/Footer.vue';

export default {
  name: 'RegisterPage',
  components: {
    AppSidebar,
    AppFooter
  },
  setup() {
    const router = useRouter();
    const account = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const agreement = ref(false);
    const loading = ref(false);
    const errors = ref({
      account: '',
      password: '',
      confirmPassword: '',
      agreement: ''
    });

    const validateForm = () => {
      let isValid = true;
      errors.value = {
        account: '',
        password: '',
        confirmPassword: '',
        agreement: ''
      };

      if (!account.value.trim()) {
        errors.value.account = '请输入账号';
        isValid = false;
      }

      if (!password.value.trim()) {
        errors.value.password = '请输入密码';
        isValid = false;
      } else if (password.value.length < 6) {
        errors.value.password = '密码长度不能少于6位';
        isValid = false;
      }

      if (!confirmPassword.value.trim()) {
        errors.value.confirmPassword = '请确认密码';
        isValid = false;
      } else if (confirmPassword.value !== password.value) {
        errors.value.confirmPassword = '两次输入的密码不一致';
        isValid = false;
      }

      if (!agreement.value) {
        errors.value.agreement = '请阅读并同意用户协议和隐私政策';
        isValid = false;
      }

      return isValid;
    };

    const clearError = (field) => {
      errors.value[field] = '';
    };

    const handleRegister = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;
      
      try {
        await register(account.value, password.value);
        
        // 注册成功，跳转到首页
        router.push('/');
      } catch (error) {
        errors.value.account = error.message || '注册失败，请稍后再试';
      } finally {
        loading.value = false;
      }
    };

    return {
      account,
      password,
      confirmPassword,
      agreement,
      loading,
      errors,
      clearError,
      handleRegister
    };
  }
};
</script>

<style scoped>
.register-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.register-container {
  margin-left: 200px;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  background-color: #f5f5f5;
}

.register-box {
  width: 100%;
  max-width: 450px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  padding: 40px;
}

.register-header {
  text-align: center;
  margin-bottom: 30px;
}

.register-header h2 {
  font-size: 24px;
  color: #333;
  margin-bottom: 10px;
}

.register-header p {
  color: #666;
  font-size: 14px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-group input:focus {
  border-color: #ff5a5f;
  outline: none;
}

.form-group.has-error input {
  border-color: #ff5a5f;
}

.error-message {
  color: #ff5a5f;
  font-size: 12px;
  margin-top: 5px;
}

.form-agreement {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  position: relative;
}

.form-agreement input {
  margin-right: 8px;
  margin-top: 3px;
}

.form-agreement label {
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}

.form-agreement a {
  color: #ff5a5f;
  text-decoration: none;
}

.form-agreement a:hover {
  text-decoration: underline;
}

.form-agreement .error-message {
  position: absolute;
  bottom: -18px;
  left: 0;
}

.register-button {
  width: 100%;
  padding: 12px;
  background-color: #ff5a5f;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.register-button:hover {
  background-color: #ff3c41;
}

.register-button:disabled {
  background-color: #ffb6b9;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.register-footer {
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
  color: #666;
}

.login-link {
  color: #ff5a5f;
  text-decoration: none;
  font-weight: 500;
}

.login-link:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .register-container {
    margin-left: 0;
    padding: 20px;
  }
  
  .register-box {
    padding: 30px 20px;
  }
}
</style> 